/* eslint-disable @next/next/no-html-link-for-pages */
import { useEffect, useState, type ReactNode } from "react";
import { AtlasExplorer } from "../../app/components/AtlasExplorer";
import { AnonymousSubmissionForm } from "../../app/components/AnonymousSubmissionForm";
import { CrossStudyGeneSearch } from "../../app/components/CrossStudyGeneSearch";
import { DatasetExplorer } from "../../app/components/DatasetExplorer";
import { FooterAffiliation } from "../../app/components/FooterAffiliation";
import { GuideContent } from "../../app/components/GuideContent";
import { InchaustiAuditBrowser } from "../../app/components/InchaustiAuditBrowser";
import { LanguageText } from "../../app/components/LanguageText";
import { ReuterSkinExplorer } from "../../app/components/ReuterSkinExplorer";
import { SpeciesCoverage } from "../../app/components/SpeciesCoverage";
import { SubmissionAdmin } from "../../app/components/SubmissionAdmin";
import { TeamDirectory } from "../../app/components/TeamDirectory";
import { TcruziRunBrowser } from "../../app/components/TcruziRunBrowser";
import { useCompactHeader } from "../../app/components/useCompactHeader";
import { catalogueAudit } from "../../app/catalogue-audit-data";
import { datasets, gaps, totalCells, type Dataset } from "../../app/data";
import {
  researchRecords,
  researchRegistry,
} from "../../app/research-registry";
import { speciesEvidenceAudit } from "../../app/species-evidence-data";
import { tcruziComparisonRows } from "../../app/tcruzi-comparison-data";
import { updateAudit } from "../../app/update-audit-data";
import { sourceEndpoints } from "../../app/workflow";

type Language = "zh" | "en";

const submissionApiEndpoint =
  import.meta.env.VITE_SUBMISSION_API_URL?.trim() || "/api/submissions";
const submissionPortalUrl =
  import.meta.env.VITE_SUBMISSION_PORTAL_URL?.trim() || null;

const navigation = [
  ["/explore/", "图谱", "Atlas"],
  ["/datasets/", "数据", "Data"],
  ["/submit/", "提交", "Submit"],
  ["/guide/", "指南", "Guide"],
  ["/team/", "关于", "About"],
] as const;

const recordSlugById: Record<string, string> = {
  "brg-2021-bsf": "prjeb41744",
  "how-2022-tsetse": "erp132258",
  "brg-2023-replicative": "prjeb58781",
  "reu-2023-skin": "gse174198",
  "lai-2026-cruzi": "e-mtab-14406",
  "inc-2026-cruzi": "prjna1200704",
};

function normalisePath(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function useLanguage() {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const savedLanguage = window.localStorage.getItem("trypcell-language");
      if (savedLanguage === "en" || savedLanguage === "zh") {
        return savedLanguage;
      }
    } catch {
      // Fall back to the browser language when storage is unavailable.
    }
    return navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
  });

  useEffect(() => {
    document.body.dataset.language = language;
    document.documentElement.lang = language === "zh" ? "zh-CN" : "en";
    try {
      window.localStorage.setItem("trypcell-language", language);
    } catch {
      // Language switching still works for the current page.
    }
    window.dispatchEvent(
      new CustomEvent<Language>("trypcell-language", { detail: language }),
    );
  }, [language]);

  return { language, setLanguage };
}

function PublicHeader() {
  const { language, setLanguage } = useLanguage();
  const currentPath = normalisePath(window.location.pathname);
  const compact = useCompactHeader();

  return (
    <>
      <a className="skip-link" href="#trypcell-main">
        {language === "zh" ? "跳到主要内容" : "Skip to main content"}
      </a>
      <div className="public-service-strip">
        <span className="public-live-dot" aria-hidden="true" />
        <span>
          {language === "zh"
            ? "公开科研门户 · 浏览与提交无需账户 · 提交须经人工审核"
            : "Public research portal · no account to browse or submit · human review required"}
        </span>
      </div>
      <header
        className={`site-header public-header${compact ? " is-compact" : ""}`}
      >
        <div className="header-inner">
          <a className="public-brand" href="/" aria-label="TrypCell Atlas home">
            <span aria-hidden="true">TC</span>
            <div>
              <strong>TrypCell Atlas</strong>
              <small>Trypanosoma single-cell evidence</small>
            </div>
          </a>
          <nav className="main-nav" aria-label="Primary navigation">
            {navigation.map(([href, zh, en]) => (
              <a
                href={href}
                key={href}
                aria-current={
                  href === "/"
                    ? currentPath === "/"
                      ? "page"
                      : undefined
                    : currentPath.startsWith(href)
                      ? "page"
                      : undefined
                }
              >
                {language === "zh" ? zh : en}
              </a>
            ))}
          </nav>
          <div className="public-language-switch" aria-label="Language">
            <button
              type="button"
              className={language === "zh" ? "active" : ""}
              aria-pressed={language === "zh"}
              onClick={() => setLanguage("zh")}
            >
              中文
            </button>
            <button
              type="button"
              className={language === "en" ? "active" : ""}
              aria-pressed={language === "en"}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
          </div>
        </div>
      </header>
    </>
  );
}

function PublicFooter() {
  return (
    <footer className="site-footer public-footer">
      <div className="footer-grid">
        <div>
          <div className="footer-brand">TrypCell Atlas</div>
          <LanguageText
            as="p"
            zh="面向全球研究者的双语、可追溯锥虫单细胞证据资源。"
            en="A bilingual, traceable Trypanosoma single-cell evidence resource."
          />
        </div>
        <div>
          <LanguageText as="h3" zh="浏览" en="Explore" />
          <a href="/explore/"><LanguageText zh="交互图谱" en="Interactive atlas" /></a>
          <a href="/datasets/"><LanguageText zh="已核实数据" en="Verified data" /></a>
          <a href="/submit/"><LanguageText zh="提交研究或更正" en="Submit a study or correction" /></a>
          <a href="/guide/"><LanguageText zh="使用指南" en="Guide" /></a>
          <a href="/team/"><LanguageText zh="关于我们" en="About us" /></a>
        </div>
        <div>
          <LanguageText as="h3" zh="证据与规范" en="Evidence & standards" />
          <a href="/methods/">Curation & provenance</a>
          <a href="/standards/">Data passports &amp; citation</a>
          <a href="/species/">Evidence gaps</a>
          <a href="/updates/">Source registry</a>
          <a href="/privacy/">Privacy &amp; local storage</a>
          <a href="/data/redistribution-manifest.json">
            Redistribution rights manifest
          </a>
          <a href="/THIRD_PARTY_NOTICES.md">Third-party notices</a>
        </div>
        <div>
          <h3>Release</h3>
          <p>
            {researchRegistry.registryVersion} · {researchRegistry.releasedAt}
          </p>
          <p>Research resource only; not for clinical decision-making.</p>
          <a href="https://tritrypdb.org/" target="_blank" rel="noreferrer">
            TriTrypDB / VEuPathDB ↗
          </a>
          <a href="https://www.ebi.ac.uk/ena/browser/home" target="_blank" rel="noreferrer">
            ENA ↗
          </a>
        </div>
      </div>
      <FooterAffiliation />
    </footer>
  );
}

function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <PublicHeader />
      <main id="trypcell-main">{children}</main>
      <PublicFooter />
    </>
  );
}

function HomePage() {
  return (
    <Layout>
      <section className="home-intro public-home-intro">
        <p className="eyebrow">Trypanosoma · public single-cell evidence portal</p>
        <LanguageText
          as="h1"
          zh="可信、开放、可追溯的锥虫细胞图谱"
          en="An open, traceable cell atlas for Trypanosoma research"
        />
        <LanguageText
          as="p"
          zh="无需登录即可探索作者公开的真实细胞坐标、元数据和已获准发布的表达层。作者结果、本站格式转换和证据缺口始终分开标注。"
          en="Explore public author-released cell coordinates, metadata and source-bounded expression layers without signing in. Author results, portal conversions and evidence gaps remain explicitly separated."
        />
        <div className="public-hero-actions">
          <a className="button" href="/explore/">
            <LanguageText zh="打开交互图谱" en="Open interactive atlas" /> →
          </a>
          <a className="button button-light" href="/datasets/">
            <LanguageText zh="查看数据目录" en="Browse data catalogue" />
          </a>
          <a className="button button-light" href="/submit/">
            <LanguageText zh="提交研究或更正" en="Submit a study or correction" />
          </a>
        </div>
      </section>

      <section className="home-explorer section">
        <SpeciesCoverage />
      </section>

      <section className="metrics" aria-label="Portal statistics">
        <div>
          <strong>54,353</strong>
          <LanguageText zh="交互图谱已载入细胞" en="cells loaded interactively" />
        </div>
        <div>
          <strong>{totalCells.toLocaleString()}</strong>
          <LanguageText zh="目录中已核实细胞" en="verified cells catalogued" />
        </div>
        <div>
          <strong>{datasets.length}</strong>
          <LanguageText zh="已核实研究" en="verified studies" />
        </div>
        <div>
          <strong>{gaps.length}</strong>
          <LanguageText zh="有来源记录的证据缺口" en="sourced evidence gaps" />
        </div>
      </section>

      <section className="section public-trust-section">
        <div className="section-heading">
          <div>
            <p className="section-number">PUBLIC RESEARCH LAYER</p>
            <LanguageText
              as="h2"
              zh="科研内容公开，免账户提交与管理权限分层"
              en="Open research content, account-free submissions, isolated review"
            />
          </div>
          <LanguageText
            as="p"
            zh="浏览图谱和提交来源可核验的研究或更正均无需账户。提交进入私有队列并返回回执；只有受控的科学审核员和管理员可以改变审核状态或批准版本化发布。"
            en="No account is required to browse the atlas or submit a source-verifiable study or correction. Submissions enter a private queue and return a receipt; only controlled reviewers and administrators can change review state or approve a versioned release."
          />
        </div>
        <div className="public-boundary-grid">
          <article>
            <span>PUBLIC</span>
            <h3>Data, atlas, genes, sources</h3>
            <p>Static files · no account · cacheable · portable</p>
          </article>
          <article>
            <span>SUBMIT</span>
            <h3>New study or correction</h3>
            <p>No account · source fields · private receipt token</p>
          </article>
          <article>
            <span>CONTROLLED</span>
            <h3>Review and release approval</h3>
            <p>Private roles · audit trail · no automatic publication</p>
          </article>
        </div>
      </section>
    </Layout>
  );
}

function ExplorePage() {
  return (
    <Layout>
      <section className="page-hero atlas-page-hero">
        <p className="eyebrow">Interactive atlas · public read-only layer</p>
        <LanguageText
          as="h1"
          zh="探索作者公开的真实细胞"
          en="Explore real author-released cells"
        />
        <LanguageText
          as="p"
          zh="当前载入54,353个作者图谱细胞。坐标、分类标签、样本和处理条件均来自已核验的作者数据；可悬停、单击、框选、套索并下载细胞元数据。"
          en="This portal loads 54,353 cells from verified author atlases. Coordinates, categories, samples and conditions come from author data; hover, click, box-select, lasso and download cell metadata."
        />
      </section>
      <section className="section public-data-boundary">
        <strong>
          <LanguageText
            zh="静态门户的表达层边界"
            en="Expression-layer boundary in this static portal"
          />
        </strong>
          <LanguageText
            as="p"
            zh="Briggs 2021与Briggs 2023的CC BY 4.0作者矩阵已由本站版本化保存；Howick 2022与Reuter 2023的既有本地视图继续明确标注文件级权利边界，不扩大再发布范围。其他研究仅在权利与完整性审核通过后才加入本站表达层。作者页面只作证据外链，任何查询都不依赖作者在线服务，也不生成替代值。"
            en="The CC BY 4.0 Briggs 2021 and Briggs 2023 author matrices are pinned and versioned by TrypCell. Existing local Howick 2022 and Reuter 2023 views retain explicit file-level rights boundaries and are not expanded. Other studies join the self-hosted layer only after rights and integrity review. Author pages are evidence links only: no query depends on an author service or generates substitute values."
          />
      </section>
      <section className="section">
        <div className="catalogue-audit-links">
          <a href="/cellbrowser/?ds=howick-2022-tsetse">
            <span>UCSC CELL BROWSER · HOWICK 2022</span>
            <strong>
              <LanguageText
                zh="搜索9,225个作者基因并查看真实原始计数 →"
                en="Search 9,225 author features and inspect real raw counts →"
              />
            </strong>
          </a>
          <a href="/datasets/erp132258/">
            <span>DATA PROVENANCE</span>
            <strong>
              <LanguageText
                zh="核对388个细胞、作者UMAP和数据边界 →"
                en="Audit 388 cells, author UMAP and data boundaries →"
              />
            </strong>
          </a>
        </div>
      </section>
      <section className="section atlas-section">
        <AtlasExplorer publicPortal />
      </section>
    </Layout>
  );
}

function DatasetsPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">Data catalogue · verified public evidence</p>
        <LanguageText
          as="h1"
          zh="已核实的锥虫单细胞数据集"
          en="Verified Trypanosoma single-cell data sets"
        />
        <LanguageText
          as="p"
          zh={`当前收录${datasets.length}项研究、共${totalCells.toLocaleString()}个目录细胞。每项均提供论文、公开登录号、作者处理结果和明确的计数口径。`}
          en={`The catalogue contains ${datasets.length} studies and ${totalCells.toLocaleString()} cell entries, each with a paper, public accession, author-processed source and explicit counting boundary.`}
        />
      </section>
      <section className="section catalogue-section">
        <div className="catalogue-audit-links">
          <a href="/methods/">
            <span>METHODS</span>
            <strong>
              <LanguageText
                zh="查看收录、排除和来源等级 →"
                en="Review inclusion, exclusion and evidence rules →"
              />
            </strong>
          </a>
          <a href="/datasets/audit/">
            <span>CONTENT AUDIT</span>
            <strong>
              <LanguageText
                zh="逐项查看六项研究的证据和统计口径 →"
                en="Audit evidence and counting boundaries for all six studies →"
              />
            </strong>
          </a>
          <a href="/datasets/tcruzi-comparison/">
            <span>T. CRUZI COMPARISON</span>
            <strong>
              <LanguageText
                zh="比较两项克氏锥虫研究的数据边界 →"
                en="Compare the boundaries of both T. cruzi studies →"
              />
            </strong>
          </a>
          <a href="/genes/">
            <span>CROSS-STUDY GENES</span>
            <strong>
              <LanguageText
                zh="按作者表达特征编号检索 →"
                en="Search exact author expression identifiers →"
              />
            </strong>
          </a>
          <a href="/standards/">
            <span>RESEARCH STANDARD</span>
            <strong>查看数据护照、许可和引用边界 →</strong>
          </a>
        </div>
        <DatasetExplorer datasets={datasets} />
      </section>
      <section className="section provenance-banner">
        <div>
          <strong>Inclusion rule</strong>
          <LanguageText
            as="p"
            zh="必须直接测量锥虫细胞，并可追溯至公开论文和数据仓库。宿主细胞数据、批量RNA测序和模拟数据不作为寄生虫单细胞图谱。"
            en="Studies must directly measure Trypanosoma cells and resolve to a public paper and repository. Host-cell studies, bulk RNA-seq and simulated data are not represented as parasite single-cell atlases."
          />
        </div>
        <a href="/methods/">Read the complete standard →</a>
      </section>
    </Layout>
  );
}

function DatasetSourceLinks({ dataset }: { dataset: Dataset }) {
  return (
    <div className="public-record-links">
      <a href={dataset.doi} target="_blank" rel="noreferrer">
        Primary paper ↗
      </a>
      <a href={dataset.repositoryUrl} target="_blank" rel="noreferrer">
        {dataset.repository} ↗
      </a>
      <a href={dataset.processedUrl} target="_blank" rel="noreferrer">
        {dataset.processed} ↗
      </a>
      {dataset.atlasUrl ? (
        <a href={dataset.atlasUrl} target="_blank" rel="noreferrer">
          Author atlas ↗
        </a>
      ) : null}
      {dataset.portalUrl ? <a href={dataset.portalUrl}>TrypCell view →</a> : null}
      {dataset.cellBrowserUrl ? (
        <a href={dataset.cellBrowserUrl}>UCSC Cell Browser view →</a>
      ) : null}
    </div>
  );
}

function DatasetRecordPage({ slug }: { slug: string }) {
  const dataset = datasets.find((entry) => recordSlugById[entry.id] === slug);
  const audit = dataset
    ? catalogueAudit.datasets.find((entry) => entry.id === dataset.id)
    : undefined;

  if (!dataset) return <NotFoundPage />;

  return (
    <Layout>
      <section className="page-hero public-record-hero">
        <p className="eyebrow">
          VERIFIED DATA RECORD · {dataset.repository}
        </p>
        <h1>
          <i>{dataset.speciesFull}</i> · {dataset.author} {dataset.year}
        </h1>
        <LanguageText
          as="p"
          zh={dataset.scopeZh}
          en={dataset.scopeEn}
        />
        <DatasetSourceLinks dataset={dataset} />
      </section>
      <section className="section public-record-summary">
        <div>
          <span>CATALOGUE CELLS</span>
          <strong>{dataset.cells.toLocaleString()}</strong>
          <LanguageText
            as="p"
            zh={dataset.cellBasisZh}
            en={dataset.cellBasisEn}
          />
        </div>
        <div>
          <span>TECHNOLOGY</span>
          <strong>{dataset.technology}</strong>
          <p>{dataset.stagesEn}</p>
        </div>
        <div>
          <span>AUDIT VERDICT</span>
          <strong>{audit?.verdict ?? "verified"}</strong>
          <p>{audit?.auditNote ?? "Source links verified in the current release."}</p>
        </div>
      </section>

      {audit ? (
        <section className="section public-evidence-list">
          <div className="section-heading">
            <div>
              <p className="section-number">EVIDENCE CHAIN</p>
              <LanguageText
                as="h2"
                zh="这个记录依据什么"
                en="Evidence supporting this record"
              />
            </div>
            <p>{audit.cellBoundary}</p>
          </div>
          <div className="public-evidence-cards">
            {audit.evidence.map((evidence) => (
              <article key={evidence.url}>
                <span>{evidence.label}</span>
                <p>{evidence.supports}</p>
                <a href={evidence.url} target="_blank" rel="noreferrer">
                  Open source ↗
                </a>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {dataset.id === "lai-2026-cruzi" ? (
        <section className="section run-browser-section">
          <div className="section-heading">
            <div>
              <p className="section-number">
                E-MTAB-14406 · SAMPLE &amp; RUN PROVENANCE
              </p>
              <LanguageText
                as="h2"
                zh="从八个来源样本追溯到每个测序文件"
                en="Trace eight source samples to every sequencing file"
              />
            </div>
            <LanguageText
              as="p"
              zh="浏览作者提交的8个来源样本、8个实验、25个ENA运行和79个FASTQ条目。样本1–7用于生命周期图谱，样本8是独立验证数据。"
              en="Browse 8 author-submitted source samples, 8 experiments, 25 ENA runs and 79 FASTQ entries. Samples 1–7 form the lifecycle atlas; sample 8 is independent validation data."
            />
          </div>
          <TcruziRunBrowser manifestDownloadUrl="/data/tcruzi-runs.json" />
        </section>
      ) : null}

      {dataset.id === "inc-2026-cruzi" ? (
        <section className="section inchausti-page-section">
          <div className="section-heading">
            <div>
              <p className="section-number">
                PRJNA1200704 · MATRIX &amp; METHOD AUDIT
              </p>
              <LanguageText
                as="h2"
                zh="保留真实矩阵与尚未解决的方法边界"
                en="Preserve real matrices and unresolved method boundaries"
              />
            </div>
            <LanguageText
              as="p"
              zh="交互审计连接原始测序、作者提交的17个Zenodo文件、论文细胞计数及论文与公开代码之间的过滤阈值差异；缺失的作者UMAP不会被重建。"
              en="This interactive audit connects raw sequencing, 17 author-deposited Zenodo files, paper cell counts and filtering-threshold differences between the paper and public code. Missing author UMAP coordinates are not reconstructed."
            />
          </div>
          <InchaustiAuditBrowser auditDownloadUrl="/data/tcruzi-inchausti-audit.json" />
        </section>
      ) : null}

      {dataset.id === "reu-2023-skin" ? (
        <section className="section public-reuter-section">
          <div className="section-heading">
            <div>
              <p className="section-number">SOURCE-BOUNDED EXPRESSION VIEW</p>
              <LanguageText
                as="h2"
                zh="170个真实样本，不伪造缺失PCA"
                en="170 real samples, no fabricated PCA"
              />
            </div>
            <LanguageText
              as="p"
              zh="作者公开文件没有Figure 4的PCA坐标。本视图仅按真实采样时间和GEO列顺序排列细胞，并分别显示两个不同单位的作者表达层。"
              en="The public author files do not contain the Figure 4 PCA coordinates. This view arranges cells only by real collection time and GEO column order, and keeps the two author expression units separate."
            />
          </div>
          <ReuterSkinExplorer />
        </section>
      ) : null}
    </Layout>
  );
}

function verdictClass(verdict: string) {
  if (verdict === "corrected") return "audit-verdict corrected";
  if (verdict === "verified") return "audit-verdict verified";
  return "audit-verdict scoped";
}

function CatalogueAuditPage() {
  const supplementary = catalogueAudit.supplementaryEvidenceLayers[0];

  return (
    <Layout>
      <section className="page-hero audit-page-hero">
        <p className="eyebrow">
          CONTENT AUDIT · {catalogueAudit.auditedAt} · SIX STUDIES
        </p>
        <LanguageText
          as="h1"
          zh="每一个数字都说明来源和统计边界"
          en="Every number carries its source and counting boundary"
        />
        <LanguageText
          as="p"
          zh="逐项核验细胞数、实验技术、论文和公共仓库。核实并不表示不同研究可直接比较；所有统计口径和数据层差异都必须随记录阅读。"
          en="Cell totals, technologies, papers and repositories are audited record by record. Verification does not imply direct cross-study comparability; every counting and data-layer boundary travels with its record."
        />
        <div className="run-hero-links">
          <a href="/datasets/">
            <LanguageText zh="← 返回数据目录" en="← Data catalogue" />
          </a>
          <a href="/data/catalogue-audit.json" download>
            <LanguageText
              zh="下载机器可读核验清单 ↓"
              en="Download machine-readable audit ↓"
            />
          </a>
        </div>
      </section>

      <section className="section audit-summary-section">
        <div className="audit-policy">
          <div>
            <span>01</span>
            <LanguageText as="h2" zh="证据顺序" en="Evidence order" />
            <LanguageText
              as="p"
              zh="正式论文 → 作者存档处理文件 → 公共仓库元数据与原始测序清单 → 明确标注的本站推导汇总。"
              en={catalogueAudit.policy.evidenceOrder.join(" → ")}
            />
          </div>
          <div>
            <span>02</span>
            <LanguageText as="h2" zh="禁止替代" en="No substitution" />
            <LanguageText
              as="p"
              zh="不以模拟细胞、虚构表达值、推测标签或重建结果填补作者没有公开的数据。"
              en={catalogueAudit.policy.prohibited}
            />
          </div>
          <div>
            <span>03</span>
            <LanguageText as="h2" zh="当前结论" en="Current outcome" />
            <LanguageText
              as="p"
              zh={`${catalogueAudit.totals.studies}项研究均可追溯；本版记录${catalogueAudit.totals.correctedClaims}项科学纠正，所有研究均带有统计或解释边界。`}
              en={`${catalogueAudit.totals.studies} studies are traceable; this release records ${catalogueAudit.totals.correctedClaims} scientific corrections and ${catalogueAudit.totals.scopedClaims} explicit study boundaries.`}
            />
          </div>
        </div>

        <div className="audit-record-list">
          {catalogueAudit.datasets.map((dataset, index) => (
            <article className="audit-record" key={dataset.id}>
              <header>
                <div>
                  <span className="audit-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p>{dataset.species}</p>
                  <h2>{dataset.label}</h2>
                </div>
                <span className={verdictClass(dataset.verdict)}>
                  <LanguageText
                    zh={dataset.verdictZh}
                    en={dataset.verdict.replaceAll("-", " ")}
                  />
                </span>
              </header>

              <div className="audit-claims">
                <div>
                  <LanguageText
                    as="span"
                    zh="目录细胞条目"
                    en="CATALOGUE CELL ENTRY"
                  />
                  <strong>{dataset.cells.toLocaleString()}</strong>
                  <p>{dataset.cellBoundary}</p>
                </div>
                <div>
                  <LanguageText
                    as="span"
                    zh="文库与核验说明"
                    en="LIBRARY & AUDIT NOTE"
                  />
                  <strong>{dataset.technology}</strong>
                  <p>{dataset.auditNote}</p>
                </div>
              </div>

              <div className="audit-evidence">
                {dataset.evidence.map((source) => {
                  const external = source.url.startsWith("http");
                  return (
                    <a
                      key={`${dataset.id}-${source.label}`}
                      href={source.url}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noreferrer" : undefined}
                    >
                      <span>{source.label}</span>
                      <p>{source.supports}</p>
                      <b>{external ? "↗" : "→"}</b>
                    </a>
                  );
                })}
              </div>
            </article>
          ))}
        </div>

        <div className="supplementary-audit">
          <p className="eyebrow">SUPPLEMENTARY EVIDENCE LAYER</p>
          <LanguageText
            as="h2"
            zh="ChagasDB保留为独立的宿主反应证据入口"
            en="ChagasDB remains a separate host-response evidence layer"
          />
          <LanguageText
            as="p"
            zh={`论文报告的数据库快照收录${supplementary.paperSnapshot.curatedPapers}篇文献、${supplementary.paperSnapshot.mammalianHostSpecies}种哺乳动物宿主和${supplementary.paperSnapshot.trypanosomaCruziStrains}个T. cruzi菌株。本站仅保留来源链接和边界，不把宿主分子变化混入寄生虫表达矩阵。`}
            en={supplementary.boundary}
          />
          <div>
            <a href={supplementary.paperUrl} target="_blank" rel="noreferrer">
              ChagasDB primary paper ↗
            </a>
            <a href={supplementary.databaseUrl} target="_blank" rel="noreferrer">
              ChagasDB source website ↗
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function TcruziComparisonPage() {
  const laidlaw = datasets.find((dataset) => dataset.id === "lai-2026-cruzi")!;
  const inchausti = datasets.find((dataset) => dataset.id === "inc-2026-cruzi")!;

  return (
    <Layout>
      <section className="page-hero comparison-page-hero">
        <p className="eyebrow">
          T. CRUZI · TWO PRIMARY STUDIES · SOURCE-BOUNDED COMPARISON
        </p>
        <LanguageText
          as="h1"
          zh="两个克氏锥虫研究不能只按细胞数比较"
          en="Two T. cruzi studies cannot be compared by cell count alone"
        />
        <LanguageText
          as="p"
          zh="两项研究的目标、样本结构、参考、定量流程、质量控制和公开处理结果均不同。本页只并列已核实的差异，不生成跨研究结论。"
          en="The studies differ in aim, sample structure, reference, quantification, quality control and public processed outputs. This page aligns verified differences without generating cross-study conclusions."
        />
        <div className="run-hero-links">
          <a href="/datasets/">
            <LanguageText zh="← 返回数据目录" en="← Data catalogue" />
          </a>
          <a href="/datasets/audit/">
            <LanguageText zh="打开完整内容审计 →" en="Open content audit →" />
          </a>
        </div>
      </section>

      <section className="section comparison-section">
        <div className="comparison-warning">
          <span>COMPARABILITY WARNING</span>
          <LanguageText
            as="h2"
            zh="当前不应直接进行跨研究差异表达"
            en="Do not run direct cross-study differential expression yet"
          />
          <LanguageText
            as="p"
            zh="必须先定义统一特征空间、样本纳入规则、双细胞处理、归一化、批次校正和验证方案，否则技术差异可能被误判为生物学差异。"
            en="A harmonised feature space, inclusion rule, doublet policy, normalisation, batch correction and validation plan must be predefined; otherwise technical differences may be mistaken for biology."
          />
        </div>

        <div
          className="comparison-table"
          role="table"
          aria-label="T. cruzi study comparison"
        >
          <div className="comparison-row comparison-head" role="row">
            <div role="columnheader">
              <LanguageText zh="核验字段" en="AUDIT FIELD" />
            </div>
            <div role="columnheader">
              <strong>
                {laidlaw.author} {laidlaw.year}
              </strong>
              <span>{laidlaw.repository}</span>
            </div>
            <div role="columnheader">
              <strong>
                {inchausti.author} {inchausti.year}
              </strong>
              <span>{inchausti.repository}</span>
            </div>
          </div>
          {tcruziComparisonRows.map((row) => (
            <div className="comparison-row" role="row" key={row.fieldEn}>
              <div role="rowheader">
                <LanguageText zh={row.fieldZh} en={row.fieldEn} />
              </div>
              <div role="cell">
                <LanguageText zh={row.laidlawZh} en={row.laidlawEn} />
              </div>
              <div role="cell">
                <LanguageText zh={row.inchaustiZh} en={row.inchaustiEn} />
              </div>
            </div>
          ))}
        </div>

        <div className="comparison-source-grid">
          {[laidlaw, inchausti].map((dataset) => (
            <article key={dataset.id}>
              <p className="eyebrow">{dataset.repository} · VERIFIED SOURCES</p>
              <h2>
                {dataset.author} {dataset.year}
              </h2>
              <LanguageText
                as="p"
                zh={dataset.scopeZh}
                en={dataset.scopeEn}
              />
              <DatasetSourceLinks dataset={dataset} />
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function GenesPage() {
  const mappingRows = [
    ["Briggs 2021", "9,045 / 9,066", "21 unmatched"],
    ["Howick 2022", "9,225 / 9,225", "0 unmatched"],
    ["Briggs 2023 · BSF", "9,690 / 9,704", "14 unmatched"],
    ["Briggs 2023 · PCF", "9,690 / 9,704", "14 unmatched"],
    ["Reuter 2023", "9,752 / 11,707", "1,955 unmatched"],
    ["Laidlaw 2026", "15,319 / 15,321", "2 unmatched"],
  ];

  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">Gene search · exact source identifiers</p>
        <LanguageText
          as="h1"
          zh="一次检索，定位多个研究中的真实表达入口"
          en="One search, multiple source-faithful expression views"
        />
        <LanguageText
          as="p"
          zh="检索36,741个不重复的作者表达特征。本站合并的是“在哪些研究中可用”，不会把原始计数、归一化值和缩放值拼接成伪比较矩阵。"
          en="Search 36,741 unique author expression features. The portal merges availability across studies, never raw counts, normalized values and scaled values into a misleading comparison matrix."
        />
      </section>
      <section className="section cross-gene-section">
        <CrossStudyGeneSearch />
      </section>
      <section className="section gene-mapping-section">
        <div className="section-heading">
          <div>
            <p className="section-number">VERSIONED IDENTIFIER POLICY</p>
            <LanguageText
              as="h2"
              zh="保留作者编号，只添加可核验映射"
              en="Keep author IDs; add only verified mappings"
            />
          </div>
          <LanguageText
            as="p"
            zh="版本映射不会改写表达矩阵特征名。当前编号只有在固定数据库快照中精确核验后才显示；未匹配记录保持原样。"
            en="Version mapping never rewrites expression-matrix features. A current ID appears only after an exact match in a pinned database snapshot; unmatched records remain unchanged."
          />
        </div>
        <div className="mapping-audit-grid">
          {mappingRows.map(([study, count, unmatched]) => (
            <div key={study}>
              <span>{study}</span>
              <strong>{count}</strong>
              <small>{unmatched}</small>
            </div>
          ))}
        </div>
        <div className="mapping-links">
          <a href="/data/gene-id-mapping-index.json" download>
            Download 64,727 mapping audit rows ↓
          </a>
          <a href="https://tritrypdb.org/" target="_blank" rel="noreferrer">
            Open TriTrypDB ↗
          </a>
        </div>
      </section>
    </Layout>
  );
}

function SpeciesPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">
          SPECIES EVIDENCE DIRECTORY · {speciesEvidenceAudit.auditDate}
        </p>
        <LanguageText
          as="h1"
          zh="没有合格单细胞数据，也要展示真实证据"
          en="When qualifying single-cell data are absent, show the evidence"
        />
        <LanguageText
          as="p"
          zh="逐物种记录系统检索中发现的最接近公开数据，并说明为什么它不能作为寄生虫单细胞图谱。证据缺口不会用占位点图或推测标签填补。"
          en="For each taxon, this directory records the closest public evidence identified and why it cannot serve as a parasite single-cell atlas. Evidence gaps are never filled with placeholder point clouds or speculative labels."
        />
      </section>
      <section className="section evidence-directory">
        <div className="evidence-audit-summary">
          <div>
            <span>AUDIT DATE</span>
            <strong>{speciesEvidenceAudit.auditDate}</strong>
          </div>
          <div>
            <span>SEARCH SCOPE</span>
            <strong>{speciesEvidenceAudit.scope}</strong>
          </div>
          <div>
            <span>RESULT</span>
            <strong>{speciesEvidenceAudit.taxa.length} evidence gaps</strong>
          </div>
        </div>
        <p className="evidence-caveat">{speciesEvidenceAudit.caveat}</p>
        <div className="evidence-card-grid">
          {speciesEvidenceAudit.taxa.map((taxon) => (
            <article key={taxon.slug} id={taxon.slug}>
              <header>
                <span>EVIDENCE GAP</span>
                <h2>
                  <i>{taxon.species}</i>
                </h2>
                <strong>{taxon.closestEvidence}</strong>
              </header>
              <dl>
                <div>
                  <dt>Closest public evidence</dt>
                  <dd>{taxon.evidenceType}</dd>
                </div>
                <div>
                  <dt>Verified scope</dt>
                  <dd>{taxon.scope}</dd>
                </div>
                <div>
                  <dt>Why it is not an atlas</dt>
                  <dd>{taxon.exclusion}</dd>
                </div>
              </dl>
              <footer>
                <a href={taxon.primaryUrl} target="_blank" rel="noreferrer">
                  Primary record ↗
                </a>
                <a href={taxon.repositoryUrl} target="_blank" rel="noreferrer">
                  {taxon.repositoryLabel} ↗
                </a>
              </footer>
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function GuidePage() {
  return (
    <Layout>
      <section className="page-hero guide-hero">
        <p className="eyebrow">GUIDE · 使用指南</p>
        <LanguageText
          as="h1"
          zh="从一个问题开始，三分钟找到可信锥虫数据"
          en="Start with a question and find traceable Trypanosoma data"
        />
        <LanguageText
          as="p"
          zh="为刚接触锥虫和单细胞数据的研究生设计：先选择研究问题，再按真实页面完成研究、基因或细胞检索，最后核对论文、仓库和数据边界。"
          en="Designed for researchers new to Trypanosoma and single-cell data: choose a question, complete a study, gene or cell search, then verify the paper, repository and data boundary."
        />
      </section>
      <section className="section guide-section">
        <GuideContent />
      </section>
    </Layout>
  );
}

function SubmitPage() {
  return (
    <Layout>
      <section className="page-hero submit-hero">
        <p className="eyebrow">SUBMIT · NO PUBLIC ACCOUNT REQUIRED</p>
        <LanguageText
          as="h1"
          zh="提交真实、可核验的锥虫研究证据"
          en="Submit verifiable Trypanosoma research evidence"
        />
        <LanguageText
          as="p"
          zh="提交新研究或报告现有记录的问题。表单只进入私有审核队列；任何内容都不会绕过格式、科学与权利审核自动出现在公开图谱中。"
          en="Propose a new study or report a problem in an existing record. The form enters a private review queue; nothing reaches the public atlas without schema, scientific and rights review."
        />
        <div className="run-hero-links">
          <a href="/guide/#submit">
            <LanguageText zh="先阅读提交指南 →" en="Read the submission guide →" />
          </a>
          <a href="/privacy/">
            <LanguageText zh="隐私说明 →" en="Privacy notice →" />
          </a>
        </div>
      </section>
      <section className="section submission-section">
        <AnonymousSubmissionForm
          apiEndpoint={submissionApiEndpoint}
          submissionPortalUrl={submissionPortalUrl}
        />
      </section>
    </Layout>
  );
}

function MethodsPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">Methods · provenance · deployment boundary</p>
        <LanguageText
          as="h1"
          zh="先保证可复核，再追求规模"
          en="Reproducibility before scale"
        />
        <LanguageText
          as="p"
          zh="每个数据集必须留下论文、登录号、物种与菌株、样本来源、实验技术、参考版本、处理方法、文件版本、校验信息和许可状态。"
          en="Every data set must preserve its paper, accession, species and strain, sample origin, technology, reference version, processing method, file version, integrity evidence and licence status."
        />
      </section>

      <section className="section public-architecture">
        <div className="section-heading">
          <div>
            <p className="section-number">DEPLOYMENT ARCHITECTURE</p>
            <LanguageText
              as="h2"
              zh="公开浏览与提交，私有审核与发布"
              en="Public browsing and submission, private review and release"
            />
          </div>
        </div>
        <div className="architecture-flow">
          <article>
            <span>01 · PUBLIC</span>
            <h3>Research portal and submission gateway</h3>
            <p>Verified public data remain portable; an optional API receives source-verifiable proposals and returns private receipts.</p>
            <small>No public account. Atlas browsing remains available if submission is temporarily unavailable.</small>
          </article>
          <div aria-hidden="true">↔</div>
          <article>
            <span>02 · CONTROLLED</span>
            <h3>Scientific review and release control</h3>
            <p>Private reviewer roles, status changes, evidence checks and release approval.</p>
            <small>Administrative authentication remains outside public navigation.</small>
          </article>
        </div>
        <p className="architecture-rule">
          Account-free submissions enter the controlled side, but public releases
          move out only after human scientific and rights review.
        </p>
      </section>

      <section className="section workflow">
        <div className="section-heading">
          <div>
            <p className="section-number">CURATION WORKFLOW</p>
            <LanguageText
              as="h2"
              zh="自动发现，人工科学审核"
              en="Automated discovery, human scientific review"
            />
          </div>
        </div>
        <div className="workflow-line">
          {[
            ["01", "Candidate", "发现候选论文与登录号"],
            ["02", "Metadata", "整理样本、物种和方法"],
            ["03", "Validation", "检查格式、字段和校验值"],
            ["04", "Review", "科学负责人核对术语与证据"],
            ["05", "Release", "发布带版本的公开记录"],
          ].map(([number, en, zh]) => (
            <div key={number}>
              <span>{number}</span>
              <strong>{en}</strong>
              <small>{zh}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="section method-columns">
        <article>
          <p className="section-number">INCLUSION</p>
          <h2>Core atlas criteria</h2>
          <ul>
            <li>Direct measurement of <i>Trypanosoma</i> cells</li>
            <li>Public paper or clearly labelled citable preprint</li>
            <li>Public repository accession</li>
            <li>Recoverable matrix or raw reads</li>
            <li>Traceable cell and sample metadata</li>
            <li>Licences recorded separately for paper, data and code</li>
          </ul>
        </article>
        <article>
          <p className="section-number">EXCLUSION</p>
          <h2>Not represented as parasite atlas data</h2>
          <ul>
            <li>Host immune-cell scRNA-seq</li>
            <li>Bulk RNA-seq without individual parasite cells</li>
            <li>Coordinates traced from published images</li>
            <li>Simulated cells or invented annotations</li>
            <li>Unresolved or untraceable provenance</li>
            <li>Automated publication without scientific review</li>
          </ul>
        </article>
      </section>

      <section className="section terminology">
        <div className="section-heading">
          <div>
            <p className="section-number">SOURCE LAYERS</p>
            <LanguageText
              as="h2"
              zh="作者结果、格式转换和重新分析必须分开"
              en="Keep author results, conversions and reanalysis separate"
            />
          </div>
        </div>
        <div className="trust-grid">
          <article>
            <span>01</span>
            <h3>Author-processed</h3>
            <p>Author matrices, coordinates, metadata and annotations.</p>
          </article>
          <article>
            <span>02</span>
            <h3>Portal-converted</h3>
            <p>File or field conversion without changing the scientific result.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Portal-reanalysed</h3>
            <p>Allowed only with public code, parameters and pinned references.</p>
          </article>
        </div>
      </section>
    </Layout>
  );
}

function StandardsPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">
          RESEARCH DATA STANDARD · {researchRegistry.registryVersion}
        </p>
        <LanguageText
          as="h1"
          zh="每项研究都有统一、可审计的数据护照"
          en="A standard, auditable passport for every study"
        />
        <LanguageText
          as="p"
          zh="实验株、参考基因组、表达单位、坐标来源、许可和已知限制分别记录；未知字段保持未知，不以推测补齐。"
          en="Biological strain, reference genome, expression unit, coordinate provenance, licence and known limitations are recorded separately. Unknown fields remain unknown."
        />
        <div className="run-hero-links">
          <a href="/dataset-registry.json" download>
            Download registry JSON ↓
          </a>
          <a href="/dataset-registry.csv" download>
            Download passport CSV ↓
          </a>
          <a href="/schema/dataset-registry.schema.json">
            Open validation schema →
          </a>
          <a href="/data/redistribution-manifest.json">
            Open redistribution manifest →
          </a>
        </div>
      </section>
      <section className="section standards-section">
        <div className="standards-policy-grid">
          <article>
            <span>FORMAL REDISTRIBUTION GATE</span>
            <LanguageText
              as="h3"
              zh="自托管范围由文件级许可逐项决定"
              en="File-level rights determine the self-hosted scope"
            />
            <LanguageText
              as="p"
              zh="Briggs 2021与2023的Zenodo记录明确为CC BY 4.0，本站因此保存并发布经校验的格式转换副本。Howick 2022与Reuter 2023保持既有、受边界约束的本地视图；Laidlaw 2026的处理后表达矩阵未通过独立再发布审核，本站只保留细胞坐标和元数据，并把作者页面作为证据外链。"
              en="The Briggs 2021 and 2023 Zenodo records specify CC BY 4.0, so TrypCell distributes integrity-checked format conversions. Howick 2022 and Reuter 2023 retain their existing bounded local views. The Laidlaw 2026 processed expression matrix has not passed independent redistribution review: TrypCell keeps only local cell coordinates and metadata, with the author page as an evidence link."
            />
            <a href="/data/redistribution-manifest.json">
              Machine-readable release gate →
            </a>
          </article>
        </div>
        <div className="standards-release-strip">
          <article>
            <span>REGISTRY</span>
            <strong>{researchRegistry.registryVersion}</strong>
            <p>Versioned portal metadata</p>
          </article>
          <article>
            <span>STUDIES</span>
            <strong>{researchRegistry.totals.studies}</strong>
            <p>Scientifically curated records</p>
          </article>
          <article>
            <span>CATALOGUE CELLS</span>
            <strong>
              {researchRegistry.totals.catalogueCellEntries.toLocaleString()}
            </strong>
            <p>Study-specific counting boundaries apply</p>
          </article>
          <article>
            <span>ATLAS RECOMPUTATION</span>
            <strong>None</strong>
            <p>Exploratory portal statistics are labelled separately</p>
          </article>
        </div>

        <div className="section-heading standards-heading">
          <div>
            <p className="section-number">FAIR-INFORMED IMPLEMENTATION</p>
            <LanguageText
              as="h2"
              zh="公开实施状态，不宣称尚未达到的合规"
              en="Publish implementation status without overstating compliance"
            />
          </div>
        </div>
        <div className="fair-grid">
          {[
            [
              "F · FINDABLE",
              "DOIs, accessions, bilingual metadata and stable record paths",
            ],
            [
              "A · ACCESSIBLE",
              "Public metadata remains visible when an upstream service fails",
            ],
            [
              "I · INTEROPERABLE",
              "Strain, reference, count, rights and evidence fields are separated; fuller controlled mappings remain a target",
            ],
            [
              "R · REUSABLE",
              "Data, article and code rights are never inferred from each other",
            ],
          ].map(([label, description]) => (
            <article key={label}>
              <span>{label}</span>
              <h3>{label.slice(4)}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>

        <div className="section-heading standards-heading">
          <div>
            <p className="section-number">STUDY PASSPORTS</p>
            <LanguageText
              as="h2"
              zh="当前六项正式研究"
              en="The six currently released studies"
            />
          </div>
        </div>
        <div className="research-passport-list">
          {researchRecords.map((record, index) => (
            <details key={record.id} open={index === 0}>
              <summary>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <small>{record.accessions.join(" · ")}</small>
                  <strong>{record.title}</strong>
                </div>
                <b>{record.recordStatus.replaceAll("-", " ")}</b>
              </summary>
              <div className="research-passport-body">
                <div className="passport-facts">
                  {[
                    ["ORGANISM", record.organism],
                    ["ORGANISM IDENTIFIER", record.organismIdentifier],
                    ["STRAIN / ISOLATE", record.strain],
                    ["TECHNOLOGY", record.measurementTechnique],
                    [
                      "CATALOGUE CELLS",
                      `${record.cellCount.toLocaleString()} · ${record.cellCountBasis}`,
                    ],
                    ["REFERENCE", record.reference],
                    ["EXPRESSION", record.expressionLayer],
                    ["COORDINATES", record.embeddingLayer],
                    ["PORTAL OPERATION", record.portalProcessing],
                    ["ARTICLE LICENCE", record.articleLicence],
                    ["DATA LICENCE", record.dataLicence],
                    ["CODE", record.codeAvailability],
                    ["CODE LICENCE", record.codeLicence],
                    [
                      "PORTAL METADATA LICENCE",
                      record.portalMetadataLicence,
                    ],
                    ["KNOWN BOUNDARY", record.knownBoundary],
                  ].map(([label, value]) => (
                    <div key={`${record.id}-${label}`}>
                      <span>{label}</span>
                      <p>{value}</p>
                    </div>
                  ))}
                </div>
                <footer>
                  <span>Last verified {record.lastVerified}</span>
                  <a href={record.doi} target="_blank" rel="noreferrer">
                    Primary paper ↗
                  </a>
                  <a href={record.portalRecord}>Full record →</a>
                </footer>
                <div className="passport-evidence-links">
                  {record.primaryEvidence.map((evidence) => (
                    <a
                      href={evidence.url}
                      key={`${record.id}-${evidence.url}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <strong>{evidence.label} ↗</strong>
                      <span>{evidence.role}</span>
                      <small>Checked {evidence.checkedAt}</small>
                    </a>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>

        <div className="section-heading standards-heading">
          <div>
            <p className="section-number">CITATION AND RIGHTS</p>
            <LanguageText
              as="h2"
              zh="原始论文、仓库和本站记录分别引用"
              en="Cite the source paper, repository and portal record separately"
            />
          </div>
        </div>
        <div className="standards-policy-grid">
          <article>
            <span>DATA CITATION</span>
            <p>
              Cite the primary DOI and repository accession for biological
              analysis. TrypCell is not the original data producer.
            </p>
          </article>
          <article>
            <span>PORTAL CITATION</span>
            <p>
              Add the TrypCell record ID, registry version{" "}
              {researchRegistry.registryVersion} and access date when the
              portal conversion or audit informed the work. No portal DOI has
              been assigned yet.
            </p>
          </article>
          <article>
            <span>LICENCE BOUNDARY</span>
            <p>
              Unknown licences remain unknown. Inspect current upstream terms
              before downloading or redistributing data.
            </p>
          </article>
        </div>
      </section>
    </Layout>
  );
}

function PrivacyPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">PRIVACY · LOCAL STORAGE</p>
        <LanguageText
          as="h1"
          zh="公开科研内容无需注册"
          en="Public research content requires no registration"
        />
        <LanguageText
          as="p"
          zh="浏览功能只在浏览器本地保存语言选择，不加载广告或第三方受众分析。若使用免账户提交，私有审核库会保存表单中明确填写的联系方式、来源信息和审核记录。"
          en="Browsing stores only the language choice in local storage and loads no advertising or third-party audience analytics. If account-free submission is used, the private review database retains the contact, source and review information explicitly entered in the form."
        />
      </section>
      <section className="section policy-grid">
        <article>
          <LanguageText
            as="h2"
            zh="本地数据"
            en="Device-local data"
          />
          <LanguageText
            as="p"
            zh="语言偏好保存在当前浏览器中，不会成为科研用户档案。"
            en="Language preference remains in the current browser and does not become a research-user profile."
          />
        </article>
        <article>
          <LanguageText as="h2" zh="免账户提交" en="Account-free submission" />
          <LanguageText
            as="p"
            zh="公开表单无需账户，但会保存提交者姓名、机构、邮箱、可选ORCID、论文和仓库来源、科学说明、状态与审核历史。回执查询令牌只在成功提交后显示一次，不应公开分享。"
            en="The public form needs no account, but retains submitter name, institution, email, optional ORCID, paper and repository sources, scientific notes, status and review history. The receipt token is shown only after a successful submission and should not be shared."
        />
        </article>
        <article>
          <LanguageText as="h2" zh="安全日志与正式信息" en="Security logs and formal details" />
          <LanguageText
            as="p"
            zh="公开站点、提交函数与私有队列使用腾讯EdgeOne Makers、全球CDN及其KV存储；平台可能处理常规安全日志。数据控制者法定名称、隐私联系邮箱与保存期限仍待实验室确认；本站不虚构这些信息，也不宣称已符合特定地区法律。"
            en="The public site, submission functions and private queue use Tencent EdgeOne Makers, its global CDN and KV storage; the platform may process ordinary security logs. The controller’s legal name, privacy contact and retention periods still require laboratory confirmation; the portal does not invent them or claim jurisdiction-specific compliance."
          />
        </article>
      </section>
    </Layout>
  );
}

function UpdatesPage() {
  return (
    <Layout>
      <section className="page-hero">
        <p className="eyebrow">
          SOURCE REGISTRY · {updateAudit.registryVersion}
        </p>
        <LanguageText
          as="h1"
          zh="来源可以自动监测，科学内容不能自动改写"
          en="Monitor sources automatically; never rewrite science automatically"
        />
        <LanguageText
          as="p"
          zh="来源变化只会产生人工复核任务。公开细胞数、标签和表达层只有在科学审核通过并形成版本化发布后才会更新。"
          en="A changed source creates a human review item. Public cell totals, labels and expression layers change only after scientific approval and a versioned release."
        />
      </section>
      <section className="section public-source-registry">
        <div className="source-policy-grid">
          <div>
            <span>LAST AUDIT</span>
            <strong>{updateAudit.auditedAt}</strong>
          </div>
          <div>
            <span>CADENCE</span>
            <strong>
              Monthly checks · {sourceEndpoints.length} registered endpoints
            </strong>
          </div>
          <div>
            <span>PUBLICATION RULE</span>
            <strong>Human approval required</strong>
          </div>
        </div>
        <div className="release-note-panel public-release-note-panel">
          <div>
            <p className="section-number">CURRENT RELEASE</p>
            <LanguageText
              as="h2"
              zh="本次科研记录变更"
              en="Scientific record changes in this release"
            />
          </div>
          <div>
            {updateAudit.releaseNotes.map((note) => (
              <article key={note.type}>
                <span>{note.type.replaceAll("-", " ")}</span>
                <LanguageText as="p" zh={note.summaryZh} en={note.summary} />
              </article>
            ))}
          </div>
        </div>
        <div className="source-registry-table" role="table">
          {updateAudit.sources.map((source) => (
            <article key={source.id} role="row">
              <div>
                <span>{source.id.toUpperCase()}</span>
                <strong>{source.label}</strong>
              </div>
              <div>
                <small>STATUS</small>
                <strong>{source.status}</strong>
              </div>
              <div>
                <small>LAST CHECKED</small>
                <strong>{source.lastChecked}</strong>
              </div>
              <div>
                <small>NEXT REVIEW</small>
                <strong>{source.nextReview}</strong>
              </div>
              <div className="source-row-links">
                <a href={source.publicRecord}>Public record →</a>
                <a href={source.paper} target="_blank" rel="noreferrer">
                  Paper ↗
                </a>
                <a href={source.repository} target="_blank" rel="noreferrer">
                  Repository ↗
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function TeamPage() {
  return (
    <Layout>
      <TeamDirectory />
    </Layout>
  );
}

function NotFoundPage() {
  return (
    <Layout>
      <section className="page-hero public-not-found">
        <p className="eyebrow">404 · PUBLIC PORTAL</p>
        <LanguageText
          as="h1"
          zh="没有找到这个公开页面"
          en="This public page was not found"
        />
        <a className="button" href="/">
          <LanguageText zh="返回首页" en="Return home" />
        </a>
      </section>
    </Layout>
  );
}

export function PublicApp() {
  const path = normalisePath(window.location.pathname);
  const datasetMatch = path.match(/^\/datasets\/([^/]+)\/$/);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  if (path === "/") return <HomePage />;
  if (path === "/explore/") return <ExplorePage />;
  if (path === "/datasets/") return <DatasetsPage />;
  if (path === "/datasets/audit/") return <CatalogueAuditPage />;
  if (path === "/datasets/tcruzi-comparison/")
    return <TcruziComparisonPage />;
  if (datasetMatch) return <DatasetRecordPage slug={datasetMatch[1]} />;
  if (path === "/genes/") return <GenesPage />;
  if (path === "/species/") return <SpeciesPage />;
  if (path === "/submit/") return <SubmitPage />;
  if (path === "/guide/") return <GuidePage />;
  if (path === "/standards/") return <StandardsPage />;
  if (path === "/methods/") return <MethodsPage />;
  if (path === "/team/") return <TeamPage />;
  if (path === "/updates/") return <UpdatesPage />;
  if (path === "/privacy/") return <PrivacyPage />;
  if (path === "/admin/") return <SubmissionAdmin />;
  return <NotFoundPage />;
}
